import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  Radio,
  RadioGroup,
  Switch,
  TextField
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { CompanyAddressDto, CompanyDto, TitleProcessingStateDto, VehicleExporterInformationDto } from 'dtos'
import { vehicleExporterInformationValidationSchema } from 'dtos/vehicleExporterInformationDto'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { AUTOCOMPLETE_PAGESIZE, DEBOUNCE_DELAY, Junction } from 'localConstants'
import { useEffect, useState } from 'react'
import { formatAddress, useDebounce } from 'utils'

interface ExporterInformationAddEditDialogProps {
  disabled?: boolean
  initialValues: VehicleExporterInformationDto
  open: boolean
  onClose: () => void
  onSave: (values: any) => void
  isLoading: boolean
}

export default function ExporterInformationAddEdit({ disabled, initialValues, isLoading, onClose, onSave, open }: ExporterInformationAddEditDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingTitleProcessingStates: false,
    isGettingBuyerCompanies: false,
    isGettingTitleDestinationCompanies: false,
    isGettingTitleDestinationCompanyAddresses: false,
    isGettingVehicleDestinationCompanies: false,
    isGettingVehicleDestinationCompanyAddresses: false
  })

  const [buyerCompanies, setBuyerCompanies] = useState<CompanyDto[]>([])
  const [titleProcessingStates, setTitleProcessingStates] = useState<TitleProcessingStateDto[]>([])
  const [titleDestinationCompanies, setTitleDestinationCompanies] = useState<CompanyDto[]>([])
  const [titleDestinationCompanyAddresses, setTitleDestinationCompanyAddresses] = useState<CompanyAddressDto[]>([])
  const [vehicleDestinationCompanyAddresses, setVehicleDestinationCompanyAddresses] = useState<CompanyAddressDto[]>([])
  const [vehicleDestinationCompanies, setVehicleDestinationCompanies] = useState<CompanyDto[]>([])

  useEffect(() => {
    setLoadingState('isGettingTitleProcessingStates', true)
    controllers
      .getTitleProcessingStates()
      .then(response => {
        setTitleProcessingStates(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingTitleProcessingStates', false)
      })
  }, [])

  const getCompaniesAutocompleteBase = (search: string, statuses: string[]) =>
    controllers.getCompanies({
      junction: Junction.AND,
      order: 'asc',
      orderBy: 'name',
      page: 0,
      pageSize: AUTOCOMPLETE_PAGESIZE,
      search,
      statuses
    })

  const getBuyerCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingBuyerCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isBuyerCompany'])
      .then(response => {
        setBuyerCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingBuyerCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  const getTitleDestinationCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingTitleDestinationCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isTitleDestinationCompany'])
      .then(response => {
        setTitleDestinationCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingTitleDestinationCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  const getVehicleDestinationCompaniesAutocomplete = useDebounce((search: string) => {
    setLoadingState('isGettingVehicleDestinationCompanies', true)
    getCompaniesAutocompleteBase(search, ['isActive', 'isVehicleDestinationCompany'])
      .then(response => {
        setVehicleDestinationCompanies(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicleDestinationCompanies', false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleExporterInformationValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Exporter Information</DialogTitle>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting || disabled}
                      error={Boolean(touched.exporterMileage && errors.exporterMileage)}
                      helperText={touched.exporterMileage && errors.exporterMileage}
                      label='Exporter Mileage'
                      name='exporterMileage'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type='number'
                      value={values.exporterMileage}
                      InputLabelProps={{ shrink: true }}
                      // InputProps={{
                      //   endAdornment: <InputAdornment position='end'>{values.exporterMileageUnitOfMeasurement}</InputAdornment>
                      // }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl>
                      <RadioGroup
                        name='exporterMileageUnitOfMeasurement'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        row
                        value={values.exporterMileageUnitOfMeasurement}
                      >
                        <FormControlLabel disabled={isLoading || isSubmitting || disabled} value='Miles' control={<Radio />} label='Miles' />
                        <FormControlLabel disabled={isLoading || isSubmitting || disabled} value='Kilometers' control={<Radio />} label='Kilometers' />
                      </RadioGroup>

                      {
                        Boolean(/*touched.unitsOfDistance &&*/ errors.exporterMileageUnitOfMeasurement) && (
                          <FormHelperText error>{/*touched.unitsOfDistance &&*/ errors.exporterMileageUnitOfMeasurement}</FormHelperText>
                        )
                        // I cannot get Formik to set touched.unitsOfDistance = true, which is causing the error message to never be displayed.
                        // Without checking touched.unitsOfDistnace, the field still errors upon save.
                        // Maxwell Sommer 09/09/2024
                      }
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || disabled}
                      disableListWrap
                      getOptionLabel={buyerCompany => buyerCompany.name!}
                      noOptionsText={
                        loadingState.isGettingBuyerCompanies
                          ? 'Searching Buyer Companies...'
                          : 'No Buyer Companies. Start typing to search for Buyer Companies...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('buyerCompany', value || undefined)
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getBuyerCompaniesAutocomplete(value)
                      }}
                      options={buyerCompanies.map(buyerCompany => ({
                        companyGuid: buyerCompany.companyGuid,
                        name: buyerCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.buyerCompany && errors.buyerCompany)}
                          helperText={touched.buyerCompany && errors.buyerCompany}
                          label='Buyer Company'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingBuyerCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.buyerCompany
                          ? {
                              companyGuid: values.buyerCompany.companyGuid,
                              name: values.buyerCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || disabled}
                      disableListWrap
                      getOptionLabel={titleProcessingState => titleProcessingState.name!}
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('titleProcessingState', value)
                      }}
                      options={titleProcessingStates.map(titleProcessingState => ({
                        titleProcessingStateGuid: titleProcessingState.titleProcessingStateGuid,
                        name: titleProcessingState.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.titleProcessingState && errors.titleProcessingState)}
                          helperText={touched.titleProcessingState && errors.titleProcessingState}
                          label='Title Processing State'
                        />
                      )}
                      value={
                        values.titleProcessingState
                          ? {
                              titleProcessingStateGuid: values.titleProcessingState.titleProcessingStateGuid,
                              name: values.titleProcessingState.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || disabled}
                      disableListWrap
                      getOptionLabel={titleDestinationCompany => titleDestinationCompany.name!}
                      noOptionsText={
                        loadingState.isGettingTitleDestinationCompanies
                          ? 'Searching Title Destination Companies...'
                          : 'No Title Destination Companies. Start typing to search for Title Destination Companies...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('titleDestinationCompany', value || undefined)
                        setFieldValue('titleDestinationCompanyAddress', null)

                        setLoadingState('isGettingTitleDestinationCompanyAddresses', true)
                        controllers
                          .getCompanyAddresses(value?.companyGuid!, {
                            page: 0,
                            pageSize: AUTOCOMPLETE_PAGESIZE,
                            statuses: ['isTitleDestinationAddress']
                          })
                          .then(response => {
                            setTitleDestinationCompanyAddresses(response.value)
                          })
                          .finally(() => {
                            setLoadingState('isGettingTitleDestinationCompanyAddresses', false)
                          })
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getTitleDestinationCompaniesAutocomplete(value)
                      }}
                      options={titleDestinationCompanies.map(titleDestinationCompany => ({
                        companyGuid: titleDestinationCompany.companyGuid,
                        name: titleDestinationCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label='Title Destination Company'
                          error={Boolean(touched.titleDestinationCompany && errors.titleDestinationCompany)}
                          helperText={touched.titleDestinationCompany && errors.titleDestinationCompany}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingTitleDestinationCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.titleDestinationCompany
                          ? {
                              companyGuid: values.titleDestinationCompany.companyGuid,
                              name: values.titleDestinationCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || disabled}
                      disableListWrap
                      getOptionLabel={address => formatAddress(address)}
                      noOptionsText={
                        loadingState.isGettingTitleDestinationCompanyAddresses
                          ? 'Searching Title Destination Company Addresses...'
                          : 'No Title Destination Company Addresses.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('titleDestinationCompanyAddress', value)
                      }}
                      options={titleDestinationCompanyAddresses.map(titleDestinationCompanyAddress => ({
                        addressGuid: titleDestinationCompanyAddress.addressGuid,
                        addressNickname: titleDestinationCompanyAddress.addressNickname,
                        address1: titleDestinationCompanyAddress.address1,
                        address2: titleDestinationCompanyAddress.address2,
                        city: titleDestinationCompanyAddress.city,
                        stateProvince: titleDestinationCompanyAddress.stateProvince,
                        postalCode: titleDestinationCompanyAddress.postalCode,
                        country: titleDestinationCompanyAddress.country
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.titleDestinationCompanyAddress && errors.titleDestinationCompanyAddress)}
                          helperText={touched.titleDestinationCompanyAddress && errors.titleDestinationCompanyAddress}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingTitleDestinationCompanyAddresses && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='Title Destination Company Address'
                        />
                      )}
                      value={
                        values.titleDestinationCompanyAddress
                          ? {
                              addressGuid: values.titleDestinationCompanyAddress.addressGuid,
                              addressNickname: values.titleDestinationCompanyAddress.addressNickname,
                              address1: values.titleDestinationCompanyAddress.address1,
                              address2: values.titleDestinationCompanyAddress.address2,
                              city: values.titleDestinationCompanyAddress.city,
                              stateProvince: values.titleDestinationCompanyAddress.stateProvince,
                              postalCode: values.titleDestinationCompanyAddress.postalCode,
                              country: values.titleDestinationCompanyAddress.country
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || disabled}
                      disableListWrap
                      getOptionLabel={vehicleDestinationCompany => vehicleDestinationCompany.name!}
                      noOptionsText={
                        loadingState.isGettingVehicleDestinationCompanies
                          ? 'Searching Vehicle Destination Companies...'
                          : 'No Vehicle Destination Companies. Start typing to search for Vehicle Destination Companies...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('vehicleDestinationCompany', value || undefined)
                        setFieldValue('vehicleDestinationCompanyAddress', null)

                        setLoadingState('isGettingVehicleDestinationCompanyAddresses', true)
                        controllers
                          .getCompanyAddresses(value?.companyGuid!, {
                            page: 0,
                            pageSize: AUTOCOMPLETE_PAGESIZE,
                            statuses: ['isVehicleDestinationAddress']
                          })
                          .then(response => {
                            setVehicleDestinationCompanyAddresses(response.value)
                          })
                          .finally(() => {
                            setLoadingState('isGettingVehicleDestinationCompanyAddresses', false)
                          })
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getVehicleDestinationCompaniesAutocomplete(value)
                      }}
                      options={vehicleDestinationCompanies.map(vehicleDestinationCompany => ({
                        companyGuid: vehicleDestinationCompany.companyGuid,
                        name: vehicleDestinationCompany.name
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.vehicleDestinationCompany && errors.vehicleDestinationCompany)}
                          helperText={touched.vehicleDestinationCompany && errors.vehicleDestinationCompany}
                          label='Vehicle Destination Company'
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingVehicleDestinationCompanies && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                      value={
                        values.vehicleDestinationCompany
                          ? {
                              companyGuid: values.vehicleDestinationCompany.companyGuid,
                              name: values.vehicleDestinationCompany.name
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting || disabled}
                      disableListWrap
                      getOptionLabel={address => formatAddress(address)}
                      noOptionsText={
                        loadingState.isGettingVehicleDestinationCompanyAddresses
                          ? 'Searching Vehicle Destination Company Addresses...'
                          : 'No Vehicle Destination Company Addresses.'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('vehicleDestinationCompanyAddress', value)
                      }}
                      options={vehicleDestinationCompanyAddresses.map(vehicleDestinationCompanyAddress => ({
                        addressGuid: vehicleDestinationCompanyAddress.addressGuid,
                        addressNickName: vehicleDestinationCompanyAddress.addressNickname,
                        address1: vehicleDestinationCompanyAddress.address1,
                        address2: vehicleDestinationCompanyAddress.address2,
                        city: vehicleDestinationCompanyAddress.city,
                        stateProvince: vehicleDestinationCompanyAddress.stateProvince,
                        postalCode: vehicleDestinationCompanyAddress.postalCode,
                        country: vehicleDestinationCompanyAddress.country
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.vehicleDestinationCompanyAddress && errors.vehicleDestinationCompanyAddress)}
                          helperText={touched.vehicleDestinationCompanyAddress && errors.vehicleDestinationCompanyAddress}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingVehicleDestinationCompanyAddresses && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='Vehicle Destination Company Address'
                        />
                      )}
                      value={
                        values.vehicleDestinationCompanyAddress
                          ? {
                              addressGuid: values.vehicleDestinationCompanyAddress.addressGuid,
                              addressNickName: values.vehicleDestinationCompanyAddress.addressNickname,
                              address1: values.vehicleDestinationCompanyAddress.address1,
                              address2: values.vehicleDestinationCompanyAddress.address2,
                              city: values.vehicleDestinationCompanyAddress.city,
                              stateProvince: values.vehicleDestinationCompanyAddress.stateProvince,
                              postalCode: values.vehicleDestinationCompanyAddress.postalCode,
                              country: values.vehicleDestinationCompanyAddress.country
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl>
                      <FormLabel error={Boolean(touched.exporterConvertCluster && errors.exporterConvertCluster)}>Convert Cluster</FormLabel>

                      <RadioGroup
                        name='exporterConvertCluster'
                        onChange={e => {
                          setFieldValue('exporterConvertCluster', e.target.value === 'true')
                        }}
                        row
                        value={values.exporterConvertCluster}
                      >
                        <FormControlLabel disabled={isLoading || isSubmitting || disabled} value='true' control={<Radio />} label='Yes' />
                        <FormControlLabel disabled={isLoading || isSubmitting || disabled} value='false' control={<Radio />} label='No' />
                      </RadioGroup>

                      {Boolean(/*touched.exporterConvertCluster &&*/ errors.exporterConvertCluster) && (
                        <FormHelperText error>{/*touched.exporterConvertCluster &&*/ errors.exporterConvertCluster}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isPreviousUSVehicleBox2A}
                          disabled={isLoading || isSubmitting || disabled}
                          name='isPreviousUSVehicleBox2A'
                          onBlur={handleBlur}
                          // Using handleChange results in the formik error ' isPreviousUSVehicleBox2A: "isPreviousUSVehicleBox2A must be a `boolean` type, but the final value was: `["on"]`." '
                          // onChange={handleChange}
                          onChange={e => {
                            setFieldValue('isPreviousUSVehicleBox2A', e.target.checked)
                          }}
                          value={values.isPreviousUSVehicleBox2A}
                        />
                      }
                      label='Previous US Vehicle - Box 2A'
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <ExplanationAccordion variant='outlined'>
                    If “Vehicle is 25 or more years old” = Yes, the "Previous US Vehicle - Box 2A" switch cannot be toggled.
                  </ExplanationAccordion>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button variant='text' color='inherit' onClick={onClose}>
                  CANCEL
                </Button>

                <LoadingButton
                  disabled={disabled}
                  loading={isLoading || isSubmitting}
                  type='submit'
                  onClick={() => {
                    console.log(errors)
                  }}
                  variant='contained'
                >
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
